import { Fragment, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { formatCurrency } from '@/lib/formatters';
import type { CreditMonth } from '@/lib/api/client';

/** Which figure the goal is measured against in this table. Net is the app's standing rule. */
export type CreditBasis = 'net' | 'gross';
export const CREDIT_BASES: readonly CreditBasis[] = ['net', 'gross'];

export interface CreditByMonthTableProps {
  months: CreditMonth[];
  /** Required, never defaulted: a table silently on the wrong basis reports the wrong months hit. */
  basis: CreditBasis;
  /** `null` clears that month's override, putting it back on the standing goal. */
  onSetOverride: (month: string, target: number | null) => void;
  /** The month whose line items are open, or null. Owned by the page: it lives in the URL. */
  expandedMonth: string | null;
  /** Called with the month to open, or null to close the open one. */
  onToggleMonth: (month: string | null) => void;
  /** What to draw under the open month. The container owns that fetch; the table takes data. */
  expandedContent: ReactNode;
}

/**
 * '2026-09' → 'Sep 2026'.
 *
 * Built from an explicit UTC instant and formatted in UTC, because the input is a month LABEL and
 * not an instant: `new Date('2026-09-01')` parses as UTC midnight and renders as 31 August in
 * every negative-offset zone — which is the zone the web suite runs in.
 */
export function formatMonthLabel(month: string): string {
  const [year, index] = month.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC', month: 'short', year: 'numeric',
  }).format(new Date(Date.UTC(year, index - 1, 1)));
}

const money = (value: number) => formatCurrency(value, { cents: true });

export function CreditByMonthTable({
  months, basis, onSetOverride, expandedMonth, onToggleMonth, expandedContent,
}: CreditByMonthTableProps) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (months.length === 0) {
    return (
      <p data-testid="credit-months-empty" className="text-sm text-gray-500">
        No months to show yet.
      </p>
    );
  }

  const startEditing = (month: CreditMonth) => {
    setEditing(month.month);
    setDraft(month.goal === null ? '' : String(month.goal));
    setError(null);
  };

  const closeEditor = () => {
    setEditing(null);
    setError(null);
  };

  const attemptSave = (monthKey: string) => {
    const parsed = Number(draft);
    // An empty box is not zero, and a negative goal is not a goal: leave the editor open, with a
    // message, rather than writing a number nobody typed.
    if (draft.trim() === '' || !Number.isFinite(parsed) || parsed < 0) {
      setError('Enter a number 0 or greater.');
      return;
    }
    onSetOverride(monthKey, parsed);
    closeEditor();
  };

  const clear = (monthKey: string) => {
    onSetOverride(monthKey, null);
    closeEditor();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, monthKey: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      attemptSave(monthKey);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Escape cancels rather than saves — it restores the draft to the current goal by tearing
      // down the editor; reopening it via the pencil re-seeds the draft from the month's own
      // `goal`, so nothing the user typed after opening survives.
      closeEditor();
    }
  };

  // Blur always cancels rather than saving: losing focus is not a deliberate confirmation, and
  // treating it as one would let an accidental click elsewhere on the page write a goal nobody
  // asked to set. The Save/Clear buttons take `onMouseDown` with `preventDefault()` so a real
  // click on them (mousedown then click) never blurs the input first — this handler firing before
  // their `onClick` would otherwise close the editor and swallow the click.
  const handleBlur = () => {
    closeEditor();
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Month</TableHead>
          <TableHead>Net</TableHead>
          <TableHead>Credit sold</TableHead>
          <TableHead>Goal</TableHead>
          <TableHead>Remaining</TableHead>
          <TableHead>Hit</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {months.map((m) => {
          const label = formatMonthLabel(m.month);
          const isOpen = expandedMonth === m.month;
          const gap = basis === 'net'
            ? { hit: m.hit, over: m.over, remaining: m.remaining }
            : { hit: m.grossHit, over: m.grossOver, remaining: m.grossRemaining };
          return (
            <Fragment key={m.month}>
            <TableRow data-testid={`credit-month-${m.month}`}>
              <TableCell data-testid="cell-month">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-label={`${isOpen ? 'Hide' : 'Show'} what made up ${label}`}
                  onClick={() => onToggleMonth(isOpen ? null : m.month)}
                  className="text-brand-600 hover:underline"
                >
                  <span aria-hidden="true" className="mr-1 inline-block w-3">{isOpen ? '▾' : '▸'}</span>
                  {label}
                </button>
                {m.isCurrent && <span className="ml-2 text-xs text-gray-500">in progress</span>}
              </TableCell>
              {/* The figure the goal is measured on carries the weight; the other stays legible. */}
              <TableCell data-testid="cell-net" className={basis === 'net' ? 'font-semibold' : 'text-gray-500'}>
                {money(m.net)}
              </TableCell>
              <TableCell data-testid="cell-gross" className={basis === 'gross' ? 'font-semibold' : 'text-gray-500'}>
                {money(m.gross)}
              </TableCell>
              <TableCell data-testid="cell-goal">
                {editing === m.month ? (
                  <span className="flex flex-col gap-1">
                    <span className="flex items-center gap-2">
                      <input
                        aria-label={`Goal for ${label}`}
                        value={draft}
                        inputMode="decimal"
                        autoFocus
                        onChange={(e) => {
                          setDraft(e.target.value);
                          setError(null);
                        }}
                        onKeyDown={(e) => handleKeyDown(e, m.month)}
                        onBlur={handleBlur}
                        className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
                      />
                      <button
                        type="button"
                        aria-label={`Save goal for ${label}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => attemptSave(m.month)}
                        className="text-brand-600 hover:underline text-sm"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        aria-label={`Clear goal for ${label}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => clear(m.month)}
                        className="text-gray-500 hover:underline text-sm"
                      >
                        Clear
                      </button>
                    </span>
                    {error && (
                      <span role="alert" data-testid="goal-error" className="text-xs text-red-600">
                        {error}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {/* Its own element: the cell also holds the pencil, so a test reading the
                        cell's textContent could not assert the value exactly. */}
                    <span data-testid="cell-goal-value">{m.goal === null ? '—' : money(m.goal)}</span>
                    <button
                      type="button"
                      aria-label={`Edit goal for ${label}`}
                      onClick={() => startEditing(m)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      &#9998;
                    </button>
                  </span>
                )}
              </TableCell>
              <TableCell data-testid="cell-gap">
                {m.goal === null
                  ? '—'
                  : gap.hit
                    ? `${money(gap.over ?? 0)} over`
                    : `${money(gap.remaining ?? 0)} to go`}
              </TableCell>
              <TableCell data-testid="cell-hit">
                {gap.hit ? <span className="text-green-700 font-medium">Hit</span> : ''}
              </TableCell>
            </TableRow>
            {isOpen && (
              <TableRow data-testid={`credit-month-${m.month}-lines`}>
                <TableCell colSpan={6} className="bg-gray-50 whitespace-normal">
                  {expandedContent}
                </TableCell>
              </TableRow>
            )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
